import type { ComponentPropsWithRef, ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface ScrollAreaProps extends ComponentPropsWithRef<"div"> {
  /** Orientation du défilement ("vertical", "horizontal", "both"). Par défaut "vertical". */
  orientation?: "vertical" | "horizontal" | "both";
  /** Variante de scrollbar ("default", "thin", "none"). Par défaut "thin". */
  scrollbarVariant?: "default" | "thin" | "none";
  /** Hauteur maximale du conteneur (ex: "max-h-64", "max-h-96"). */
  maxHeight?: string;
  /** Active l'alignement magnétique (scroll snap). */
  snap?: boolean;
  /**
   * Estompe les bords vers lesquels il reste du contenu à faire défiler.
   *
   * L'effet est un masque piloté par la position de défilement elle-même
   * (`animation-timeline: scroll()`), donc calculé sur le fil du compositeur :
   * aucun écouteur `scroll`, aucun travail sur le fil principal, et une zone
   * qui reste fluide même pendant un rendu React coûteux.
   */
  fade?: boolean;
  children?: ReactNode;
}

const ORIENTATION_CLASSES = {
  vertical: "overflow-y-auto overflow-x-hidden flex-col",
  horizontal: "overflow-x-auto overflow-y-hidden flex-row",
  both: "overflow-auto",
} as const;

const SCROLLBAR_CLASSES = {
  default: "scrollbar-default",
  thin: "scrollbar-thin",
  none: "scrollbar-none",
} as const;

/**
 * Composant de zone de défilement ScrollArea SOTA.
 * Offre un défilement tactile à inertie natif (`-webkit-overflow-scrolling: touch`)
 * avec styles de barre de défilement personnalisés et fluides.
 */
export function ScrollArea({
  className,
  orientation = "vertical",
  scrollbarVariant = "thin",
  maxHeight,
  snap,
  fade,
  children,
  ...props
}: ScrollAreaProps) {
  return (
    <div
      className={cn(
        "relative w-full [webkit-overflow-scrolling:touch]",
        ORIENTATION_CLASSES[orientation],
        SCROLLBAR_CLASSES[scrollbarVariant],
        snap && "snap-x snap-mandatory",
        fade && (orientation === "horizontal" ? "scroll-fade-x" : "scroll-fade-y"),
        maxHeight,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
